/* Run inspector — one journey-run record: where am I, what do I know,
 * who is it waiting on. Context tree + history timeline + current surface. */
import React, {
    useEffect,
    useState
} from 'react';
import {
    ArrowLeft,
    Loader2,
    RefreshCw
} from 'lucide-react';
import {
    appEngineService
} from '../../api/appEngine.service';
import {
    useAppStudio
} from '../../AppStudioContext';
import JsonTree from '../../shared/JsonTree';
import {
    RunStatusBadge
} from '../../shared/badges';
import type {
    RunRecord
} from '../../api/types';

export function fmtStamp(v: any): string {
    if (v == null) return '—';
    const d = typeof v === 'number' ? new Date(v * (v < 1e12 ? 1000 : 1)) : new Date(String(v));
    return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
}

function Fact({
    label,
    children
}: {
    label: string;
    children: React.ReactNode;
}) {
    return (
        <div>
            <div className="aps-stat-label">{label}</div>
            <div className="aps-mono" style={{
                fontSize: 'var(--aps-text-sm)',
                marginTop: 2
            }}>
                {children}
            </div>
        </div>
    );
}

export default function RunInspector({
    runId,
    onBack
}: {
    runId: string;
    onBack: () => void;
}) {
    const {
        tenancy
    } = useAppStudio();
    const [run, setRun] = useState<RunRecord | null>(null);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        appEngineService.getRun(tenancy, runId)
            .then(setRun)
            .catch(() => setRun(null))
            .finally(() => setLoading(false));
    };

    useEffect(load, [tenancy, runId]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading && !run) {
        return (
            <div className="aps-empty">
                <Loader2 size={22} className="aps-spin" />
                <span>Loading run {runId}…</span>
            </div>
        );
    }
    if (!run) {
        return (
            <div className="aps-empty">
                <p style={{
                    color: 'var(--aps-danger)'
                }}>
                    Run {runId} could not be loaded.
                </p>
                <button className="aps-btn aps-btn-secondary" onClick={onBack}>Back to runs</button>
            </div>
        );
    }

    const history = Array.isArray(run.history) ? run.history : [];

    return (
        <div>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 16
            }}>
                <button className="aps-icon-btn" onClick={onBack} title="Back to runs">
                    <ArrowLeft size={16} />
                </button>
                <h2 className="aps-h3 aps-mono">{run.id || runId}</h2>
                <RunStatusBadge status={run.status} />
                <span className="aps-topbar-spacer" />
                <button className="aps-icon-btn" onClick={load} title="Refresh">
                    <RefreshCw size={15} className={loading ? 'aps-spin' : undefined} />
                </button>
            </div>
            <div className="aps-card" style={{
                padding: '16px 18px',
                marginBottom: 16,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: 14
            }}>
                <Fact label="Journey">{run.journey || '—'}</Fact>
                <Fact label="Current surface">{run.current_surface || '—'}</Fact>
                <Fact label="Run by">{run.run_by || '—'}</Fact>
                <Fact label="Waiting on">{run.waiting_on || '—'}</Fact>
                <Fact label="Created">{fmtStamp(run.created_at)}</Fact>
                <Fact label="Updated">{fmtStamp(run.updated_at)}</Fact>
                {run.opened_from ? <Fact label="Opened from">{run.opened_from}</Fact> : null}
                {run.handoff_to ? <Fact label="Handoff to">{run.handoff_to}</Fact> : null}
            </div>
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(320px, 1fr) minmax(280px, 380px)',
                gap: 16,
                alignItems: 'start'
            }}>
                <div className="aps-card" style={{
                    padding: '14px 16px'
                }}>
                    <p className="aps-meta" style={{
                        margin: '0 0 10px'
                    }}>
                        Context — the ONE accumulating $.context
                    </p>
                    <JsonTree value={run.context ?? {}} />
                </div>
                <div className="aps-card" style={{
                    padding: '14px 16px'
                }}>
                    <p className="aps-meta" style={{
                        margin: '0 0 12px'
                    }}>
                        History — surface boundaries
                    </p>
                    {history.length === 0 && (
                        <p className="aps-muted" style={{
                            fontSize: 'var(--aps-text-sm)',
                            margin: 0
                        }}>
                            No boundary crossed yet.
                        </p>
                    )}
                    <div className="aps-timeline">
                        {history.map((h, i) => (
                            <div key={i} className="aps-timeline-item">
                                <div className="aps-mono" style={{
                                    fontWeight: 600,
                                    fontSize: 'var(--aps-text-sm)'
                                }}>
                                    {h.surface || `step ${i + 1}`}
                                </div>
                                <div style={{
                                    fontSize: 'var(--aps-text-xs)',
                                    color: 'var(--aps-text-muted)',
                                    fontFamily: 'var(--aps-font-mono)'
                                }}>
                                    {Object.entries(h)
                                        .filter(([k]) => k !== 'surface')
                                        .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
                                        .join(' · ') || '—'}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
